import { useEffect, useCallback, useRef } from 'react';
import { useForceUpdate } from '@huse/update';
import { useOriginalDeepCopy } from '@huse/previous-value';
import { acceptLatest, createStrategy, keepEarliest, keepEarliestSuccess, waitAccept, findQuery, } from 'query-shape';
const builtInStrategies = {
    acceptLatest: createStrategy(acceptLatest),
    keepEarliest: createStrategy(keepEarliest),
    keepEarliestSuccess: createStrategy(keepEarliestSuccess),
    waitAccept: createStrategy(waitAccept),
};
export function useRequestCallback(task, params, options) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { strategy = 'acceptLatest', idempotent = false } = options || {};
    if (!builtInStrategies.hasOwnProperty(strategy)) {
        throw new Error(`Strategy named ${strategy} is not supported in useRequest`);
    }
    const implement = builtInStrategies[strategy];
    const unmounted = useRef(false);
    const querySets = useRef(new WeakMap());
    const forceUpdate = useForceUpdate();
    const safeForceUpdate = useCallback(() => {
        if (!unmounted.current) {
            forceUpdate();
        }
    }, [forceUpdate]);
    const fetch = useCallback((key) => {
        const querySet = querySets.current.get(task);
        querySets.current.set(task, implement.fetch(querySet, key));
        safeForceUpdate();
    }, [safeForceUpdate, implement, task]);
    const receive = useCallback((key, data) => {
        const querySet = querySets.current.get(task);
        querySets.current.set(task, implement.receive(querySet, key, data));
        safeForceUpdate();
    }, [safeForceUpdate, implement, task]);
    const error = useCallback((key, error) => {
        const querySet = querySets.current.get(task);
        querySets.current.set(task, implement.error(querySet, key, error));
        safeForceUpdate();
    }, [safeForceUpdate, implement, task]);
    const key = useOriginalDeepCopy(params);
    const querySet = querySets.current.get(task);
    const query = querySet && findQuery(querySet, key);
    const skipEffect = idempotent && !!query;
    const request = useCallback(() => {
        if (skipEffect) {
            return;
        }
        if (!querySets.current.has(task)) {
            querySets.current.set(task, implement.initialize());
        }
        fetch(key);
        task(key).then(data => receive(key, data), ex => error(key, ex));
    }, [error, fetch, implement, key, receive, skipEffect, task]);
    const acceptCurrentKey = useCallback(() => {
        const querySet = querySets.current.get(task);
        querySets.current.set(task, implement.accept(querySet, key));
        safeForceUpdate();
    }, [safeForceUpdate, implement, key, task]);
    useEffect(() => {
        unmounted.current = false;
        return () => {
            unmounted.current = true;
        };
    }, []);
    if (!query) {
        return [
            request,
            {
                accept: acceptCurrentKey,
                pending: false,
            },
        ];
    }
    return [
        request,
        {
            accept: acceptCurrentKey,
            pendingCount: query.pendingMutex,
            pending: query.pendingMutex > 0,
            data: (_b = (_a = query.response) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : undefined,
            error: (_d = (_c = query.response) === null || _c === void 0 ? void 0 : _c.error) !== null && _d !== void 0 ? _d : undefined,
            nextData: (_f = (_e = query.nextResponse) === null || _e === void 0 ? void 0 : _e.data) !== null && _f !== void 0 ? _f : undefined,
            nextError: (_h = (_g = query.nextResponse) === null || _g === void 0 ? void 0 : _g.error) !== null && _h !== void 0 ? _h : undefined,
        },
    ];
}
export function useRequest(task, params, options) {
    const [request, result] = useRequestCallback(task, params, options);
    // Since `useRequestCallback` triggers request manually, its initial `pending` is `false`,
    // however in `useRequest` we need initial `pending` to be `true`.
    const resultFixedForEffect = Object.assign(Object.assign({}, result), { pending: result.pendingCount === undefined ? true : result.pending });
    useEffect(() => {
        request();
    }, [request]);
    return resultFixedForEffect;
}
//# sourceMappingURL=index.js.map